import { useEffect, useMemo, useRef } from "react";
import { useStore } from "./state/store";
import { computeView } from "./engine/compute";
import { computeAnalogs } from "./engine/analog";
import { countQualifyingYears } from "./data/loader";
import type { Filters } from "./engine/types";
import Canvas from "./map/Canvas";
import ResultsPanel from "./features/ResultsPanel";
import OniRangeSlider from "./features/OniRangeSlider";
import LiveSidebar from "./features/LiveSidebar";
import LivePanel from "./features/LivePanel";
import ExplorerSidebar from "./features/ExplorerSidebar";
import ExplorerPanel from "./features/ExplorerPanel";
import TimeDock, { fmtUtc, type TimeTrack } from "./features/TimeDock";
import FocusPanel from "./features/FocusPanel";
import ErrorBoundary from "./features/ErrorBoundary";
import WelcomeDisclaimer from "./features/WelcomeDisclaimer";
import StalenessBanner from "./features/StalenessBanner";
import CommandPalette from "./features/CommandPalette";
import SheetHandle from "./features/SheetHandle";
import { hydrateFromHash, initPermalinks } from "./state/permalink";
import { sheetHeight, useViewportHeight, watchLayout } from "./state/layout";

// ?embed=1 → chromeless map view for blogs/newsrooms (sidebar + panel hidden)
const EMBED = new URLSearchParams(window.location.search).has("embed");

const MONTHS = [
  [6, "Jun"], [7, "Jul"], [8, "Aug"], [9, "Sep"], [10, "Oct"], [11, "Nov"],
] as const;

const MONTH_FULL: Record<number, string> = {
  6: "June", 7: "July", 8: "August", 9: "September", 10: "October", 11: "November",
};

const titleCase = (s: string) =>
  s === "UNNAMED" ? "Unnamed" : s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const ONI_PRESETS: { label: string; range: [number, number] }[] = [
  { label: "La Niña", range: [-3, -0.5] },
  { label: "Neutral", range: [-0.5, 0.5] },
  { label: "Mild El Niño", range: [0.5, 1.5] },
  { label: "Strong El Niño", range: [1.5, 3] },
  { label: "All", range: [-3, 3] },
];

export default function App() {
  const load = useStore((s) => s.load);
  const loaded = useStore((s) => s.loaded);
  const loadError = useStore((s) => s.loadError);
  const mode = useStore((s) => s.mode);
  const setMode = useStore((s) => s.setMode);
  const tracks = useStore((s) => s.tracks);
  const boxA = useStore((s) => s.boxA);
  const boxB = useStore((s) => s.boxB);
  const boxC = useStore((s) => s.boxC);
  const filters = useStore((s) => s.filters);
  const oniYears = useStore((s) => s.oniYears);
  const setFilters = useStore((s) => s.setFilters);
  const clearBoxes = useStore((s) => s.clearBoxes);
  const climoOverlay = useStore((s) => s.climoOverlay);
  const toggleClimoOverlay = useStore((s) => s.toggleClimoOverlay);
  const liveIndex = useStore((s) => s.live.index);
  const liveSelectedId = useStore((s) => s.live.selectedId);
  const liveBundle = useStore((s) => s.live.bundle);
  const showAnalogs = useStore((s) => s.live.showAnalogs);
  const analogTightness = useStore((s) => s.live.analogTightness);
  const analogMatchMode = useStore((s) => s.live.analogMatchMode);
  const selectLiveStorm = useStore((s) => s.selectLiveStorm);
  const runScrubActive = useStore((s) => s.live.runScrubActive);
  const cycles = useStore((s) => s.live.cycles);
  const cycleIdx = useStore((s) => s.live.cycleIdx);
  const scrubBundle = useStore((s) => s.live.scrubBundle);
  const setCycleIdx = useStore((s) => s.setCycleIdx);
  const compare = useStore((s) => s.compare);
  const setCompareHour = useStore((s) => s.setCompareHour);
  const explorer = useStore((s) => s.explorer);
  const setHour = useStore((s) => s.setHour);
  const focus = useStore((s) => s.focus);
  const narratives = useStore((s) => s.narratives);
  const focusStorm = useStore((s) => s.focusStorm);
  const clearFocus = useStore((s) => s.clearFocus);
  const setFocusHour = useStore((s) => s.setFocusHour);
  const futureOn = useStore((s) => s.overlays.future);
  const futureInfo = useStore((s) => s.live.futureInfo);
  const futureIdx = useStore((s) => s.live.futureIdx);
  const setFutureIdx = useStore((s) => s.setFutureIdx);
  const outlookArea = useStore((s) => s.live.outlookArea);
  const layout = useStore((s) => s.ui.layout);
  const sheet = useStore((s) => s.ui.sheet);
  const menuOpen = useStore((s) => s.ui.menuOpen);
  const setLayout = useStore((s) => s.setLayout);
  const setSheet = useStore((s) => s.setSheet);
  const setMenuOpen = useStore((s) => s.setMenuOpen);
  const vh = useViewportHeight();
  const narrow = layout !== "desktop";
  const phone = layout === "phone";
  const resultsRef = useRef<HTMLElement>(null);

  // ── narrow screens (state/layout.ts): desktop is untouched by all of this ──
  useEffect(() => watchLayout(setLayout), [setLayout]);
  // picking a storm closes the slide-in menu; on a phone the sheet rises to
  // show it (and drops back to the map for the overview or an outlook area)
  const picked = focus.stormId ?? (mode === "live" ? liveSelectedId : mode === "explorer" ? explorer.selectedId : null);
  useEffect(() => {
    const ui = useStore.getState().ui;
    if (ui.layout === "desktop") return;
    if (picked) setMenuOpen(false);
    if (ui.layout === "phone") {
      setSheet(picked ? "half" : "peek");
      if (resultsRef.current) resultsRef.current.scrollTop = 0;
    }
  }, [picked, setMenuOpen, setSheet]);
  useEffect(() => {
    if (outlookArea && useStore.getState().ui.layout === "phone") setSheet("peek");
  }, [outlookArea, setSheet]);

  useEffect(() => {
    void load().then(async () => {
      // rebuild a shared view from the hash, THEN start mirroring changes into
      // it (order matters — mirroring first would overwrite the incoming link)
      await hydrateFromHash();
      initPermalinks();
    });
  }, [load]);

  // Esc dismisses focus
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") clearFocus(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [clearFocus]);

  // Feature B: when the overlay storm changes, auto-condition the base rate on
  // that storm's month (the "historically, in October…" framing). User can override.
  const overlayMonthRef = useRef<string | null>(null);
  useEffect(() => {
    if (!climoOverlay || !liveBundle) return;
    if (overlayMonthRef.current === liveBundle.storm_id) return;
    overlayMonthRef.current = liveBundle.storm_id;
    const bt = liveBundle.best_track;
    const last = bt[bt.length - 1];
    if (last?.dt) {
      const m = new Date(last.dt).getUTCMonth() + 1;
      if (m >= 6 && m <= 11) setFilters({ monthFilter: [m] });
    }
  }, [climoOverlay, liveBundle, setFilters]);

  const oni = useMemo(() => {
    const [lo, hi] = filters.oniRange;
    if (lo === -3 && hi === 3) return { range: null as [number, number] | null, qy: null as number | null };
    return {
      range: [lo, hi] as [number, number],
      qy: countQualifyingYears(oniYears, [lo, hi], filters.yearMin, filters.yearMax),
    };
  }, [filters, oniYears]);

  const view = useMemo(() => {
    const ef: Filters = {
      yearMin: filters.yearMin,
      yearMax: filters.yearMax,
      oniRange: oni.range,
      monthFilter: filters.monthFilter,
      qualifyingYears: oni.qy,
    };
    return computeView(tracks, boxA, boxB, boxC, ef);
  }, [tracks, boxA, boxB, boxC, filters, oni]);

  const oniNote = oni.range
    ? `ONI ${oni.range[0] >= 0 ? "+" : ""}${oni.range[0].toFixed(1)} to ${oni.range[1] >= 0 ? "+" : ""}${oni.range[1].toFixed(1)} · over ${oni.qy} qualifying yr${oni.qy === 1 ? "" : "s"} (frequencies per qualifying year)`
    : null;

  // Feature A: trajectory analogs — genesis-anchored, adaptive boxes, downstream.
  const analog = useMemo(() => {
    if (mode !== "live" || !showAnalogs || !liveBundle || tracks.length === 0) return null;
    return computeAnalogs(liveBundle.best_track, tracks, analogTightness, analogMatchMode);
  }, [mode, showAnalogs, liveBundle, tracks, analogTightness, analogMatchMode]);

  const toggleMonth = (m: number) => {
    const cur = filters.monthFilter;
    setFilters({ monthFilter: cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m] });
  };

  // ── time tracks for the unified TimeDock (priority order = default active) ──
  // Story markers: the engine's narrative cards, rendered as jump-to diamonds.
  const ACCENT: Record<string, string> = {
    CDI: "#FB7185", IKE: "#38BDF8", FSTI: "#F59E0B", IFI: "#22DD66", SGM: "#A78BFA",
  };
  const CARD_LABEL: Record<string, string> = {
    pre_authored: "STORY", ri_alert: "RAPID INTENSIFICATION", stall: "STALLING",
    landfall: "LANDFALL", peak: "PEAK INTENSITY", fsti_spike: "TORNADO THREAT", weakening: "WEAKENING",
  };
  const storyMarkers = (stormId: string | null, length: number) =>
    (stormId ? narratives[stormId] ?? [] : [])
      .filter((c) => c.h >= 0 && c.h < length)
      .map((c) => ({
        index: c.h,
        color: ACCENT[c.a] ?? "#9FB3C8",
        title: `${c.i} ${CARD_LABEL[c.y] ?? c.y.toUpperCase()}`,
        body: c.t,
      }));

  const timeTracks: TimeTrack[] = [];
  // futurecast first: when it's on, it is what the dock plays
  if (mode === "live" && futureOn && futureInfo && futureInfo.steps.length >= 2) {
    const st = futureInfo.steps;
    const local = (ms: number) =>
      new Date(ms).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", timeZoneName: "short" });
    timeTracks.push(futureInfo.official ? {
      id: "future", label: "📋 WPC official rainfall", color: "#34D399",
      length: st.length, index: futureIdx, setIndex: setFutureIdx, unit: "Period",
      format: (i) => `${st[i].label ?? `+${st[i].fhr} h`} · through ${local(st[i].time)}`,
    } : futureInfo.kind === "blend" && futureInfo.product === "chance" ? {
      id: "future", label: `📊 NBM chance of ${futureInfo.thresholdIn}″+`, color: "#22D3EE",
      length: st.length, index: futureIdx, setIndex: setFutureIdx, unit: "Period",
      format: (i) => `${st[i].label ?? `+${st[i].fhr} h`} · through ${local(st[i].time)}`,
    } : futureInfo.kind === "blend" ? {
      id: "future", label: "📊 NBM blended rain", color: "#22D3EE",
      length: st.length, index: futureIdx, setIndex: setFutureIdx, unit: "Step",
      format: (i) => `+${st[i].fhr} h · ${local(st[i].time)}`,
    } : {
      id: "future", label: `🔮 ${futureInfo.label} futurecast`, color: "#F472B6",
      length: st.length, index: futureIdx, setIndex: setFutureIdx, unit: "Step",
      format: (i) => `+${st[i].fhr} h · ${local(st[i].time)}` +
        (st[i].remnant ? " · remnants" : `${st[i].vmax ? ` · ${st[i].vmax} kt` : ""}${st[i].pmin ? ` · ${st[i].pmin} mb` : ""}`),
    });
  }
  if (focus.source === "replay" && focus.replay && focus.replay.frames.length >= 2) {
    const fr = focus.replay.frames;
    timeTracks.push({
      id: "focus", label: `🎯 ${focus.replay.name}`, color: "#60A5FA",
      length: fr.length, index: focus.hour, setIndex: setFocusHour, unit: "Hour",
      format: (i) => `${fmtUtc(fr[i]?.dt, true)} · ${fr[i]?.vmax ?? "—"} kt`,
      markers: storyMarkers(focus.stormId, fr.length),
    });
  }
  if (mode === "explorer" && !focus.stormId && explorer.replay && explorer.replay.frames.length >= 2) {
    const fr = explorer.replay.frames;
    timeTracks.push({
      id: "explorer", label: `🌀 ${explorer.replay.name}`, color: "#60A5FA",
      length: fr.length, index: explorer.hour, setIndex: setHour, unit: "Hour",
      format: (i) => `${fmtUtc(fr[i]?.dt, true)} · ${fr[i]?.vmax ?? "—"} kt`,
      markers: storyMarkers(explorer.selectedId, fr.length),
    });
  }
  if (mode === "live" && runScrubActive && cycleIdx != null && cycles.length >= 2) {
    timeTracks.push({
      id: "run", label: `🕑 ${scrubBundle?.name ?? liveBundle?.name ?? "Run history"}`, color: "#A78BFA",
      length: cycles.length, index: cycleIdx, setIndex: (i) => void setCycleIdx(i), unit: "Cycle",
      format: (i) => {
        const c = cycles[i];
        return `${fmtUtc(c?.cycle)} run · ${c?.vmax ?? "—"} kt${c?.pmin ? ` · ${c.pmin} mb` : ""}${c?.spread48_nm != null ? ` · 48h spread ${c.spread48_nm} nm` : ""}`;
      },
      detail: (i) => {
        const cur = cycles[i];
        const prev = i > 0 ? cycles[i - 1] : null;
        // `hotWhen` = the sign that means intensification / more uncertainty
        // (wind up, spread up, but pressure DOWN)
        const chip = (label: string, v: number | null, unit: string, hotWhen: 1 | -1) => {
          if (v == null || v === 0) return null;
          return (
            <span key={label} style={{ color: Math.sign(v) === hotWhen ? "#FB7185" : "#22DD66", marginLeft: 8, fontWeight: 600 }}>
              {v > 0 ? "▲" : "▼"} {label} {v > 0 ? "+" : "−"}{Math.abs(v)}{unit}
            </span>
          );
        };
        const pOk = (p: number | null | undefined) => p != null && p > 800;
        return (
          <>
            {chip("vmax", prev && cur?.vmax != null && prev.vmax != null ? cur.vmax - prev.vmax : null, " kt", 1)}
            {chip("pmin", prev && pOk(cur?.pmin) && pOk(prev.pmin) ? cur!.pmin! - prev.pmin! : null, " mb", -1)}
            {chip("spread", prev && cur?.spread48_nm != null && prev.spread48_nm != null ? cur.spread48_nm - prev.spread48_nm : null, " nm", 1)}
          </>
        );
      },
    });
  }
  {
    const cFrames = compare.source === "replay" ? compare.replay?.frames : undefined;
    const cPts = compare.source === "track" ? compare.track?.track : undefined;
    const n = cFrames?.length ?? cPts?.length ?? 0;
    if (compare.stormId && n >= 2) {
      const cname = compare.replay?.name
        ?? (compare.track ? `${titleCase(compare.track.name)} (${compare.track.year})` : compare.stormId);
      timeTracks.push({
        id: "compare", label: `🆚 ${cname}`, color: "#F59E0B",
        length: n, index: compare.hour, setIndex: setCompareHour, unit: "Point",
        format: (i) =>
          cFrames
            ? `${fmtUtc(cFrames[i]?.dt, true)} · ${cFrames[i]?.vmax ?? "—"} kt`
            : `${compare.track?.year ?? ""} track · ${cPts?.[i]?.vmax ?? "—"} kt`,
      });
    }
  }

  return (
    <div
      className={`app layout-${layout}${narrow && menuOpen ? " menu-open" : ""}`}
      style={EMBED || phone ? {
        ...(EMBED ? { gridTemplateColumns: "1fr" } : {}),
        ...(phone ? { ["--sheet-h" as string]: `${sheetHeight(sheet, vh)}px` } : {}),
      } : undefined}
    >
      <WelcomeDisclaimer />
      <CommandPalette />
      {narrow && menuOpen && !EMBED && <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />}
      {!EMBED && <aside className="sidebar">
        {narrow && <button className="menu-close" onClick={() => setMenuOpen(false)} aria-label="Close the menu">✕</button>}
        <div className="brand">🌀 Storm Context Engine</div>
        <div className="mode-toggle">
          <button className={mode === "climatology" ? "mode active" : "mode"} onClick={() => setMode("climatology")}>
            📊 Climatology
          </button>
          <button className={mode === "explorer" ? "mode active" : "mode"} onClick={() => setMode("explorer")}>
            🌀 Explorer
          </button>
          <button className={mode === "live" ? "mode active" : "mode"} onClick={() => setMode("live")}>
            🛰️ Live
          </button>
        </div>
        <hr />

        {mode === "climatology" && (
          <>
            <label className="muted xs">YEAR RANGE</label>
            <div className="row">
              <input
                type="number" value={filters.yearMin} min={1851} max={2025}
                onChange={(e) => setFilters({ yearMin: Number(e.target.value) })}
              />
              <input
                type="number" value={filters.yearMax} min={1851} max={2025}
                onChange={(e) => setFilters({ yearMax: Number(e.target.value) })}
              />
            </div>

            <hr />
            <label className="muted xs">🌡️ ONI REGIME</label>
            <OniRangeSlider value={filters.oniRange} onChange={(r) => setFilters({ oniRange: r })} />
            <div className="preset-grid" style={{ marginTop: 10 }}>
              {ONI_PRESETS.map((p) => {
                const active = filters.oniRange[0] === p.range[0] && filters.oniRange[1] === p.range[1];
                return (
                  <button key={p.label} className={active ? "chip active" : "chip"} onClick={() => setFilters({ oniRange: p.range })}>
                    {p.label}
                  </button>
                );
              })}
            </div>

            <hr />
            <label className="muted xs">PEAK MONTH</label>
            <div className="preset-grid">
              {MONTHS.map(([m, name]) => (
                <button key={m} className={filters.monthFilter.includes(m) ? "chip active" : "chip"} onClick={() => toggleMonth(m)}>
                  {name}
                </button>
              ))}
            </div>

            <hr />
            <label className="muted xs">🛰️ LIVE OVERLAY (GATEWAY)</label>
            <button
              className={climoOverlay ? "chip active" : "chip"}
              style={{ width: "100%", marginTop: 4 }}
              onClick={() => void toggleClimoOverlay()}
            >
              {climoOverlay ? "Live model tracks: ON" : "Show live model tracks"}
            </button>
            {climoOverlay && (
              <>
                <select
                  className="storm-select" style={{ marginTop: 6 }}
                  value={liveSelectedId ?? ""}
                  onChange={(e) => e.target.value && void selectLiveStorm(e.target.value)}
                >
                  <option value="" disabled>Select storm…</option>
                  {liveIndex.map((s) => (
                    <option key={s.storm_id} value={s.storm_id}>
                      {s.label ?? `${s.name ?? s.storm_id} (${s.year ?? ""})`}
                    </option>
                  ))}
                </select>
                <p className="muted xs" style={{ marginTop: 6 }}>
                  Draw boxes over the model clusters — the probability is the historical base rate.
                </p>
              </>
            )}

            <hr />
            <button className="clear-btn" onClick={clearBoxes}>🗑️ Clear Boxes</button>

            {loadError && <div className="error">Load error: {loadError}</div>}
            {!loaded && !loadError && <div className="muted sm">Loading track database…</div>}
            {loaded && <div className="muted xs" style={{ marginTop: 12 }}>{tracks.length} storms loaded</div>}
          </>
        )}
        {mode === "explorer" && <ExplorerSidebar />}
        {mode === "live" && <LiveSidebar />}
        <div className="legal-links">
          <a href="/privacy/" target="_blank" rel="noopener">Privacy</a> · <a href="/terms/" target="_blank" rel="noopener">Terms</a>
        </div>
      </aside>}

      <main className="canvas-wrap">
        {narrow && !EMBED && (
          <button className={menuOpen ? "menu-btn on" : "menu-btn"} onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">☰</button>
        )}
        <ErrorBoundary label="the map" inline>
          <Canvas legByStormId={view.legByStormId} analog={analog} />
        </ErrorBoundary>
        <StalenessBanner />
        {focus.stormId && (
          <div className="focus-chip">
            <span>🎯 {focus.replay ? focus.replay.name : focus.track ? `${titleCase(focus.track.name)} (${focus.track.year})` : "Storm"}</span>
            <button onClick={clearFocus} title="Dismiss (Esc)">✕</button>
          </div>
        )}
        {/* ONE TimeDock for every time-indexed context (focus / explorer /
            run history / compare) — switchable tracks, play/pause, ←/→ keys */}
        <TimeDock tracks={timeTracks} />
      </main>

      {!EMBED && <section className="results-wrap" ref={resultsRef}>
        {phone && <SheetHandle vh={vh} />}
        {focus.stormId && <FocusPanel focus={focus} onClose={clearFocus} />}
        {!focus.stormId && mode === "climatology" && climoOverlay && view.chain && view.chain.count_a > 0 && (() => {
          const c = view.chain;
          const monthLabel = filters.monthFilter.length === 1 ? MONTH_FULL[filters.monthFilter[0]] : null;
          const pct = c.prob_b_given_a_pct;
          const col = pct >= 20 ? "#22DD66" : pct >= 10 ? "#FBBF24" : "#60A5FA";
          const analogs = c.storms_ab.slice(0, 3).map((s) => titleCase(s.name)).join(", ");
          return (
            <div className="gateway-headline">
              <div className="muted xs">🛰️ GATEWAY · {liveBundle?.name ?? "live storm"}</div>
              <p style={{ margin: "6px 0 0" }}>
                Of <b>{c.count_a}</b> storms through Box A{monthLabel ? ` in ${monthLabel}` : ""},{" "}
                <b style={{ color: col }}>{pct}%</b> ({c.count_ab}) reached Box B.
              </p>
              {analogs && <p className="muted sm" style={{ margin: "4px 0 0" }}>incl. {analogs}</p>}
            </div>
          );
        })()}
        {/* focus-gated like the other modes' panels — otherwise focusing a storm
            from a climatology list stacks two full-height panels */}
        {!focus.stormId && mode === "climatology" && <ResultsPanel view={view} hasBoxA={!!boxA} hasBoxB={!!boxB} oniNote={oniNote} />}
        {mode === "explorer" && !focus.stormId && (
          <ExplorerPanel replay={explorer.replay} hour={explorer.hour} loading={explorer.loading} />
        )}
        {!focus.stormId && mode === "live" && showAnalogs && analog && (
          <div className="gateway-headline">
            <div className="muted xs">🔥 TRAJECTORY ANALOGS · 1851–2025</div>
            {analog.genesisMode ? (
              <p style={{ margin: "6px 0 0" }}>
                <b>{analog.count}</b> storms ({analog.hurricanes} hurricanes){" "}
                {analog.matchMode === "transit"
                  ? "tracked through this storm's origin region"
                  : "were born near this storm's origin"}{" "}
                and followed a similar path ({analog.boxes.length}-box match).
                The heatmap shows where they tracked next.
              </p>
            ) : (
              <p style={{ margin: "6px 0 0" }}>
                Not enough track yet for trajectory matching — showing climatology:{" "}
                <b>{analog.count}</b> storms that passed through this zone.
              </p>
            )}
            {analog.count > 0 && analog.count < 6 && (
              <p className="muted sm" style={{ margin: "4px 0 0", color: "#FBBF24" }}>
                ⚠ Small sample (n={analog.count}) — few true analogs. Loosen to widen.
              </p>
            )}
            {analog.count === 0 && (
              <p className="muted sm" style={{ margin: "4px 0 0", color: "#FBBF24" }}>
                No analogs at this tightness — loosen to widen the search.
              </p>
            )}
          </div>
        )}
        {mode === "live" && showAnalogs && analog && analog.analogs.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div className="muted xs" style={{ marginBottom: 4 }}>
              ANALOG STORMS ({analog.analogs.length})
              {analog.analogs.length > 60 ? " · newest 60 drawn on map" : ""} · hover a track to identify it
            </div>
            <div className="analog-storm-list">
              {analog.analogs.slice(0, 60).map((a) => (
                <div
                  className={focus.stormId === a.storm_id ? "storm-row clickable focused" : "storm-row clickable"}
                  key={a.storm_id}
                  onClick={() => void focusStorm(a.storm_id)}
                  title="Open in Explorer"
                >
                  <span>{titleCase(a.name)} ({a.year})</span>
                  <span className="muted">{a.ss_category} · {a.peak_vmax}kt</span>
                </div>
              ))}
              {analog.analogs.length > 60 && (
                <div className="muted xs center" style={{ padding: "6px 0" }}>
                  … and {analog.analogs.length - 60} more — tighten to narrow
                </div>
              )}
            </div>
          </div>
        )}
        {mode === "live" && !focus.stormId && <LivePanel />}
      </section>}
    </div>
  );
}
